import React, { useEffect, useId, useRef, useState } from 'react';

export const isOfferFormat = (format) => String(format?.productFormatCode || format?.ProductFormat?.code || format?.clientType || '')
  .trim().toLowerCase() === 'oferta';
export const isWeightOffer = (format) => format?.useScaleWeight === true
  || /^(kg|kgs|kilo|kilos|kilogramo|kilogramos)$/i.test(String(format?.metricUnit || '').trim());

export const OfferAvailabilitySummary = ({ policy }) => {
  if (!policy) return null;
  return <div className="mt-1 max-w-48 space-y-0.5 text-[11px] leading-4 text-slate-600">
    {policy.endedAt ? <p className="font-semibold">{policy.endedReason === 'expired' ? 'Plazo terminado'
      : policy.endedReason === 'stock_limit' ? 'Stock de Oferta agotado' : policy.endedReason === 'limits_required' ? 'Falta duración o stock máximo' : 'Oferta desactivada'}</p> : (
      <>
        {policy.startsAt && <p>Desde {new Date(policy.startsAt).toLocaleString('es-CL', {
          timeZone: 'America/Santiago', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
        })}</p>}
        {policy.expiresAt && <p>Hasta {new Date(policy.expiresAt).toLocaleString('es-CL', {
          timeZone: 'America/Santiago', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
        })}</p>}
        {policy.maxQuantity != null && <p>{Number(policy.soldQuantity || 0).toLocaleString('es-CL')} / {Number(policy.maxQuantity).toLocaleString('es-CL')} {policy.unit} vendidos</p>}
        {policy.tolerance > 0 && <p>Cierra con {Number(policy.tolerance).toLocaleString('es-CL')} {policy.unit || 'kg'} restantes</p>}
      </>
    )}
  </div>;
};

const OfferAvailabilityDialog = ({ target, saving = false, error = '', onClose, onSubmit }) => {
  const id = useId();
  const panel = useRef(null);
  const weight = isWeightOffer(target.format);
  const previous = target.format.offerAvailability;
  const [days, setDays] = useState(String(previous?.durationDays || 3));
  const [stock, setStock] = useState(previous?.maxQuantity == null ? '' : String(previous.maxQuantity));
  const [tolerance, setTolerance] = useState(String(previous?.tolerance || 0));
  const [validation, setValidation] = useState('');
  const unit = weight ? 'kg' : target.format.metricUnit || 'unidades';
  const mixedUnits = new Set((target.formats || [target.format]).map((format) => isWeightOffer(format) ? 'kg' : String(format.metricUnit || 'unidades').trim().toLowerCase())).size > 1;

  useEffect(() => {
    const previousFocus = document.activeElement;
    panel.current?.focus();
    return () => previousFocus?.focus?.();
  }, []);

  const submit = (event) => {
    event.preventDefault();
    if (saving) return;
    const maxQuantity = Number(stock);
    const margin = weight ? Number(tolerance) : 0;
    if (mixedUnits) return setValidation('Activa por separado las Ofertas con distintas unidades de venta.');
    if (!Number.isInteger(Number(days)) || Number(days) < 1 || Number(days) > 365) return setValidation('Indica una duración de 1 a 365 días.');
    if (!stock.trim() || !Number.isFinite(maxQuantity) || maxQuantity <= 0
      || maxQuantity > 999999999 || (!weight && !Number.isInteger(maxQuantity))
      || Math.abs(maxQuantity * 1000 - Math.round(maxQuantity * 1000)) > 0.00001) return setValidation('Indica un stock máximo válido.');
    if (!Number.isFinite(margin) || margin < 0 || margin >= maxQuantity || Math.abs(margin * 1000 - Math.round(margin * 1000)) > 0.00001) return setValidation('La tolerancia debe ser menor al stock máximo.');
    setValidation('');
    onSubmit({ durationDays: Number(days), maxQuantity, tolerance: margin });
  };

  const onKeyDown = (event) => {
    if (event.key === 'Escape' && !saving) { event.preventDefault(); onClose(); }
    if (event.key !== 'Tab') return;
    const elements = Array.from(panel.current.querySelectorAll('button:not(:disabled), input:not(:disabled), [tabindex="0"]'));
    const first = elements[0];
    const last = elements[elements.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === panel.current)) {
      event.preventDefault(); last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
  };

  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
    <section ref={panel} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby={`${id}-title`}
      onKeyDown={onKeyDown} className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white p-6 shadow-xl outline-none">
      <h2 id={`${id}-title`} className="text-lg font-bold text-slate-900">Activar Oferta con límites</h2>
      <p className="mt-1 text-sm text-slate-600">{target.product.name} · {target.variety.name}</p>
      <p className="mt-3 text-sm text-slate-600">Duración y stock máximo son obligatorios. Se marcará como agotada al cumplirse el primer límite. Solo cuentan las ventas completadas en esta sucursal desde la activación.</p>
      {target.formats?.length > 1 && <p className="mt-3 text-sm text-slate-600">Los límites y el contador se aplican por separado a cada formato Oferta.</p>}
      {mixedUnits && <p role="alert" className="mt-3 text-sm text-red-700">Activa por separado las Ofertas con distintas unidades de venta.</p>}
      {previous && !previous.endedAt && <p className="mt-3 rounded-lg bg-amber-50 p-3 text-sm text-amber-900">Al activar, comienza un nuevo plazo y el contador de ventas vuelve a cero.</p>}
      <form onSubmit={submit} className="mt-5 flex min-h-0 flex-1 flex-col">
        <div className="min-h-0 space-y-5 overflow-y-auto pb-4">
        <fieldset disabled={saving} className="space-y-3">
          <label className="block text-sm text-slate-700">Días desde ahora
            <input type="number" min="1" max="365" step="1" required value={days}
              onChange={(event) => setDays(event.target.value)} className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2" />
          </label>
        </fieldset>
        <fieldset disabled={saving} className="space-y-3">
            <label className="block text-sm text-slate-700">Stock máximo ({unit})
              <input type="number" min={weight ? '0.001' : '1'} max="999999999" step={weight ? '0.001' : '1'} required
                value={stock} onChange={(event) => setStock(event.target.value)} className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2" />
            </label>
            {weight && <div>
              <label className="block text-sm text-slate-700">Tolerancia de peso (kg)
              <input type="number" min="0" step="0.001" required value={tolerance} aria-describedby={`${id}-tolerance-help`}
                onChange={(event) => setTolerance(event.target.value)} className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2" />
              </label>
              <p id={`${id}-tolerance-help`} className="mt-1 text-xs text-slate-500">Cierra antes del máximo para cubrir diferencias de peso. Por ejemplo: 100 kg con 2 kg de tolerancia cierran a los 98 kg vendidos.</p>
            </div>}
            {Number(stock) > Number(tolerance) && <p className="rounded-lg bg-slate-50 p-3 text-sm text-slate-700">
              Se agota desde {(Number(stock) - (weight ? Number(tolerance) : 0)).toLocaleString('es-CL')} {unit} vendidos.
            </p>}
            <p className="text-xs text-slate-500">La venta que alcanza el límite se completa; las siguientes ya no pueden usar esta Oferta.</p>
        </fieldset>
        {(validation || error) && <p role="alert" className="text-sm text-red-700">{validation || error}</p>}
        </div>
        <div className="flex shrink-0 justify-end gap-3 border-t border-slate-100 bg-white pt-4">
          <button type="button" disabled={saving} onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold">Cancelar</button>
          <button type="submit" disabled={saving || mixedUnits} className="rounded-lg bg-emerald-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">{saving ? 'Activando…' : 'Activar Oferta'}</button>
        </div>
      </form>
    </section>
  </div>;
};

export default OfferAvailabilityDialog;
